import styled from 'styled-components'
import { MobileColors } from '../../styles/mobileTokens'
import { Z_INDEX } from '../../styles/z-index'

// Colors - now references centralized design tokens matching Tailwind config
export const COLORS = {
  primary: MobileColors.primary, // #06512f (fb-green-900)
  background: MobileColors.backgroundWhite, // #ffffff
  cardBackground: MobileColors.backgroundCard, // #EEF5F2 (pistachio-200)
  border: MobileColors.borderDefault, // #E2E2E2
  lightGray: MobileColors.backgroundOverlay, // #f9fafb (gray-50)
  mediumGray: MobileColors.textSecondary, // #6E6E6E
  text: MobileColors.textPrimary, // #191D23
  textLight: MobileColors.textSecondary, // #6E6E6E
  badge: MobileColors.badgeBackground, // #f0f0f0
  badgeText: MobileColors.textSecondary, // #6E6E6E
}

// Page Container (for dedicated /search route)
export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: ${COLORS.background};
`

// Back Button
export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  border-bottom: 1px solid ${COLORS.border};
  background: ${MobileColors.accent}; /* Medium green (#2C6B4E) for visual separation from header */
  color: white;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  height: 40px;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`

// Search Container
export const SearchContainer = styled.div`
  padding: 12px 16px 6px 16px;
  background: ${COLORS.background};
`

// Dropdown
export const DropdownButton = styled.button`
  width: auto;
  padding: 6px 8px;
  background: transparent;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: ${COLORS.text};
  cursor: pointer;
  margin-bottom: 8px;

  &:hover {
    opacity: 0.7;
  }
`

export const DropdownLabel = styled.span`
  color: ${COLORS.textLight};
  font-size: 14px;
`

export const DropdownValue = styled.span`
  color: ${COLORS.primary};
  font-weight: 500;
  text-decoration: underline;
`

export const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${COLORS.background};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: ${Z_INDEX.MOBILE_DROPDOWN};
  overflow: hidden;
`

export const DropdownItem = styled.button<{ $isSelected?: boolean }>`
  width: 100%;
  padding: 14px 16px;
  background: ${(props) =>
    props.$isSelected ? COLORS.lightGray : 'transparent'};
  border: none;
  border-bottom: 1px solid ${COLORS.border};
  text-align: left;
  font-size: 14px;
  color: ${(props) => (props.$isSelected ? COLORS.primary : COLORS.text)};
  font-weight: ${(props) => (props.$isSelected ? '500' : '400')};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${COLORS.lightGray};
  }
`

// Search Input
export const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`

export const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
`

export const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 40px;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  font-size: 14px;
  color: ${COLORS.text};

  &::placeholder {
    color: ${COLORS.textLight};
  }

  &:focus {
    outline: none;
    border-color: ${COLORS.primary};
  }
`

export const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${COLORS.textLight};
  display: flex;
  align-items: center;
`

export const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${COLORS.textLight};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    color: ${COLORS.text};
  }
`

export const IconButton = styled.button`
  min-width: 44px;
  min-height: 44px;
  padding: 10px;
  background: ${COLORS.background};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${COLORS.text};

  &:hover {
    background: ${COLORS.lightGray};
  }
`

// Results Area Wrapper (contains results and panels)
export const ResultsAreaWrapper = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`

// Results
export const ResultsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  height: 100%;
  display: flex;
  flex-direction: column;
`

export const ResultsHeader = styled.div`
  padding: 4px 16px;
  font-size: 12px;
  color: ${COLORS.textLight};
  background: ${COLORS.background};
`

// Applied Filters
export const AppliedFiltersContainer = styled.div`
  padding: 4px 16px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  background: ${COLORS.background};
`

export const FilterTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${MobileColors.backgroundSelected};
  color: ${MobileColors.primary};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`

export const FilterTagLabel = styled.span`
  color: ${MobileColors.primary};
  font-size: 12px;
  font-weight: 600;
`

export const FilterTagValue = styled.span`
  color: ${MobileColors.primary};
  font-weight: 400;
`

export const FilterTagRemove = styled.button`
  background: none;
  border: none;
  color: ${MobileColors.primary};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-left: 2px;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: ${MobileColors.shadowBrand};
  }
`

export const ResultsList = styled.div`
  padding: 8px 16px 80px 16px; /* Extra bottom padding for fixed footer */
`

// Result Card
export const ResultCard = styled.div`
  display: block;
  padding: 12px;
  background: ${COLORS.cardBackground};
  border: none;
  border-radius: 8px;
  margin-bottom: 12px;
  text-decoration: none;
  color: ${COLORS.text};
  cursor: pointer;
  overflow: hidden;
  transition: background 0.1s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: ${MobileColors.backgroundHover};
    box-shadow: 0 2px 8px ${MobileColors.shadowSm};
  }

  &:active {
    background: ${MobileColors.backgroundActive};
    transition: background 0.05s ease; /* Faster response on tap */
  }
`

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`

export const CompanyInfo = styled.div`
  flex: 1;
`

export const CompanyLabel = styled.div`
  font-size: 12px;
  color: ${COLORS.textLight};
  margin-bottom: 4px;
`

export const CompanyLink = styled.div`
  font-size: 12px;
  color: ${COLORS.primary};
  text-decoration: underline;
  margin-bottom: 4px;
`

export const CompanyName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.text};
  margin-bottom: 4px;
`

export const LocationName = styled.div`
  font-size: 14px;
  color: ${COLORS.text};
  font-weight: 500;
`

export const CategoryBadge = styled.span`
  padding: 4px 10px;
  background: ${COLORS.badge};
  color: ${COLORS.badgeText};
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: 8px;
`

export const AddressLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  color: ${COLORS.primary};
  text-decoration: underline;
  margin-top: 8px;
`

export const AddressIcon = styled.span`
  color: ${COLORS.mediumGray};
  margin-top: 2px;
`

export const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${COLORS.textLight};
  margin-top: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: ${COLORS.textLight};
`

export const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`

export const EmptyStateText = styled.div`
  font-size: 16px;
  margin-bottom: 8px;
`

export const EmptyStateSubtext = styled.div`
  font-size: 14px;
  color: ${COLORS.textLight};
`

// Side Panels (Filter & Sort)
// Base panel styles shared between full-width and partial-width panels
const BasePanelStyles = `
  position: fixed;
  top: calc(
    var(--footer-height) + env(safe-area-inset-top) + 8px + 40px
  ); /* Below header (with safe area + padding) + back button */
  bottom: calc(
    60px + env(safe-area-inset-bottom)
  ); /* Above footer (with safe area) */
  background: ${COLORS.background};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow-y: auto;
`

// Full-width side panel (used by Filter)
export const SidePanel = styled.div<{ $isOpen: boolean }>`
  ${BasePanelStyles}
  left: 0;
  right: 0;
  transform: ${(props) =>
    props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease-in-out;
  z-index: ${Z_INDEX.MOBILE_SEARCH_PANEL};
`

// 65% width side panel with backdrop (used by Sort)
export const SortPanel = styled.div<{ $isOpen: boolean }>`
  ${BasePanelStyles}
  right: 0;
  width: 75%;
  max-width: 385px;
  transform: ${(props) =>
    props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease-in-out;
  z-index: ${Z_INDEX.MOBILE_SEARCH_PANEL + 1};
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
`

// Darkened backdrop overlay (used by Sort)
export const SortOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${MobileColors.overlay};
  z-index: ${Z_INDEX.MOBILE_SEARCH_PANEL};
  opacity: ${(props) => (props.$isOpen ? '1' : '0')};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
`

export const PanelOverlay = styled.div<{ $isOpen: boolean }>`
  display: none; /* No overlay needed - panel covers entire area */
`

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${COLORS.border};
  background: ${COLORS.background};
`

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${COLORS.text};
`

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.text};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  font-size: 20px;

  &:hover {
    color: ${COLORS.primary};
  }
`

export const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`

export const PanelFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid ${COLORS.border};
  background: ${COLORS.background};
`

export const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  min-height: 44px;

  ${(props) =>
    props.$variant === 'primary'
      ? `
    background: ${COLORS.primary};
    color: white;

    &:hover {
      background: ${MobileColors.primaryDark};
    }
  `
      : `
    background: transparent;
    color: ${COLORS.text};
    border: 1px solid ${COLORS.border};

    &:hover {
      background: ${COLORS.lightGray};
    }
  `}
`

// Filter/Sort Sections
export const FilterSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const FilterSectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const RadioOption = styled.label<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${(props) =>
    props.$isSelected ? COLORS.lightGray : 'transparent'};
  border: 1px solid
    ${(props) => (props.$isSelected ? COLORS.primary : COLORS.border)};
  border-radius: 8px;
  cursor: pointer;
  min-height: 44px;

  &:hover {
    background: ${COLORS.lightGray};
  }
`

export const RadioInput = styled.input`
  display: none;
`

export const RadioLabel = styled.span`
  font-size: 14px;
  color: ${COLORS.text};
`

export const RadioCheckmark = styled.span<{ $isSelected?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${(props) => (props.$isSelected ? COLORS.primary : COLORS.border)};
  background: ${(props) =>
    props.$isSelected ? COLORS.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
`

// Load More Button
export const LoadMoreButton = styled.button<{ disabled?: boolean }>`
  width: 50%;
  max-width: 200px;
  padding: 12px;
  margin: 12px auto;
  display: block;
  background: ${MobileColors.backgroundHover};
  color: ${MobileColors.accent};
  border: 1px solid ${MobileColors.accent};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};

  &:hover:not(:disabled) {
    background: ${MobileColors.backgroundActive};
  }

  &:active:not(:disabled) {
    background: ${MobileColors.backgroundActive};
    opacity: 0.9;
  }
`

// Loading Container
export const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
`
